import React, { useState } from "react";
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiCalendar,
  FiMapPin, FiUploadCloud
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import useEvents from "../../hooks/useEvent"; 

const IMAGE_BASE_URL = import.meta.env.VITE_API_BASE || "http://localhost:8080";

const EventsPage = () => {
  const { events, loading, createEvent, updateEvent, deleteEvent } = useEvents();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    if (selectedFile) formData.append("image", selectedFile);

    const success = currentEvent
      ? await updateEvent(currentEvent._id, formData)
      : await createEvent(formData);

    if (success) {
      closeModal();
    }
    setIsSubmitting(false);
  };

  // --- FIXED OPEN MODAL LOGIC ---
  const openModal = (event = null) => {
    setCurrentEvent(event);
    if (event?.image) {
      // 1. Check if it's already a full URL or a relative path
      // 2. Ensure the forward slash '/' exists between Base URL and Path
      // 3. Replace any Windows backslashes '\' with '/'
      const formattedPath = event.image.startsWith("http") 
        ? event.image 
        : `${IMAGE_BASE_URL}/${event.image.replace(/\\/g, "/")}`;
      
      setPreviewImage(formattedPath);
    } else {
      setPreviewImage(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentEvent(null);
    setSelectedFile(null);
    setPreviewImage(null);
  };

  return (
    <div className="p-6 md:p-10 space-y-8  min-h-screen text-slate-300">
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-black text-white uppercase italic tracking-tighter">
            Mission <span className="text-blue-600">Events.</span>
          </h2>
          <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.3em] mt-2 italic">
            Global Operational Registry
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg"
        >
          <FiPlus size={16} /> Deploy New Event
        </button>
      </div>

      {/* TABLE */}
      <div className="bg-[#0a0f1a] rounded-[2.5rem] border border-white/5 overflow-hidden shadow-2xl">
        <table className="w-full text-left">
          <thead className="bg-white/[0.02] border-b border-white/5 uppercase text-[9px] font-black text-slate-500 tracking-widest">
            <tr>
              <th className="px-8 py-5">Mission Detail</th>
              <th className="px-8 py-5">Timeline</th>
              <th className="px-8 py-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {events.map((event) => (
              <tr key={event._id} className="hover:bg-white/[0.01] transition-colors group">
                <td className="px-8 py-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-black border border-white/10 overflow-hidden shrink-0">
                      {event.image ? (
                        <img
                          src={`${IMAGE_BASE_URL}/${event.image.replace(/\\/g, "/")}`}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
                          alt="Event"
                          onError={(e) => { e.target.src = "https://via.placeholder.com/150?text=NO+SIGNAL"; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-white/5">
                            <FiUploadCloud className="text-slate-700" size={20} />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-black text-white uppercase italic tracking-tight">{event.title}</p>
                      <p className="text-[9px] text-blue-500 font-bold uppercase">{event.name}</p>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-6">
                  <div className="text-[10px] font-black text-slate-300 uppercase flex flex-col gap-1">
                    <span className="flex items-center gap-2 text-white italic">
                      <FiCalendar className="text-blue-500" /> {event.startDate} — {event.endDate}
                    </span>
                    <span className="flex items-center gap-2 text-slate-500">
                      <FiMapPin /> {event.location}
                    </span>
                  </div>
                </td>
                <td className="px-8 py-6 text-right">
                  <button onClick={() => openModal(event)} className="p-3 bg-white/5 text-slate-400 hover:text-white hover:bg-blue-600 rounded-xl transition-all mr-2">
                    <FiEdit2 size={12} />
                  </button>
                  <button onClick={() => deleteEvent(event._id)} className="p-3 bg-white/5 text-slate-400 hover:text-white hover:bg-red-600 rounded-xl transition-all">
                    <FiTrash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MODAL (FORM) */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeModal} className="fixed inset-0 bg-black/90 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-[#0a0f1a] border border-white/10 w-full max-w-2xl rounded-[2.5rem] overflow-hidden relative z-10 shadow-2xl">
              <form onSubmit={handleSaveEvent} className="p-8 space-y-6 max-h-[85vh] overflow-y-auto custom-scrollbar">
                
                {/* PREVIEW IMAGE SECTION */}
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-white/10 rounded-[2rem] h-48 bg-black relative overflow-hidden group hover:border-blue-500/50 transition-all">
                  {previewImage ? (
                    <img src={previewImage} className="w-full h-full object-cover" alt="Preview" />
                  ) : (
                    <div className="text-center">
                      <FiUploadCloud size={30} className="text-slate-700 mx-auto mb-2" />
                      <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest">Upload Mission Visual</p>
                    </div>
                  )}
                  <input type="file" onChange={handleImageChange} className="absolute inset-0 opacity-0 cursor-pointer" />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="text-[8px] font-black uppercase text-slate-500 ml-2">Mission Title</label>
                    <input name="title" defaultValue={currentEvent?.title} className="w-full bg-black border border-white/5 rounded-2xl p-4 text-xs font-bold text-white outline-none focus:border-blue-600" required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[8px] font-black uppercase text-slate-500 ml-2">Assignment Name</label>
                    <input name="name" defaultValue={currentEvent?.name} className="w-full bg-black border border-white/5 rounded-2xl p-4 text-xs font-bold text-white outline-none focus:border-blue-600" required />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-[8px] font-black uppercase text-slate-500 ml-2">Start Date</label>
                    <input name="startDate" type="date" defaultValue={currentEvent?.startDate} className="w-full bg-black border border-white/5 rounded-2xl p-4 text-[10px] font-bold text-white outline-none" required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[8px] font-black uppercase text-slate-500 ml-2">End Date</label>
                    <input name="endDate" type="date" defaultValue={currentEvent?.endDate} className="w-full bg-black border border-white/5 rounded-2xl p-4 text-[10px] font-bold text-white outline-none" required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[8px] font-black uppercase text-slate-500 ml-2">Time</label>
                    <input name="time" type="time" defaultValue={currentEvent?.time} className="w-full bg-black border border-white/5 rounded-2xl p-4 text-[10px] font-bold text-white outline-none" required />
                  </div>
                </div>

                <div className="space-y-1">
                    <label className="text-[8px] font-black uppercase text-slate-500 ml-2">Deployment Location</label>
                    <input name="location" defaultValue={currentEvent?.location} className="w-full bg-black border border-white/5 rounded-2xl p-4 text-xs font-bold text-white outline-none focus:border-blue-600" required />
                </div>

                <div className="space-y-1">
                    <label className="text-[8px] font-black uppercase text-slate-500 ml-2">Mission Summary</label>
                    <textarea name="description" rows="3" defaultValue={currentEvent?.description} className="w-full bg-black border border-white/5 rounded-2xl p-4 text-xs font-bold text-white outline-none focus:border-blue-600 resize-none" />
                </div>

                <button disabled={isSubmitting} type="submit" className="w-full bg-blue-600 hover:bg-blue-500 py-5 rounded-2xl text-[10px] font-black uppercase tracking-[0.3em] transition-all shadow-xl shadow-blue-900/20">
                  {isSubmitting ? "TRANSMITTING DATA..." : currentEvent ? "Update Operational Record" : "Deploy Mission Event"}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventsPage;